import React from 'react';
import { Row, Col, Form, Dropdown } from 'react-bootstrap';
import { FaToggleOn, FaToggleOff, FaInfoCircle } from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBox, faExclamationTriangle, faWarehouse, faCogs } from '@fortawesome/free-solid-svg-icons';
import './IngredientStockConfiguration.scss';

interface Option {
  id: number;
  name: string;
  unidad_medida_id?: number;
}

interface IngredientStockConfigurationProps {
  isStock: boolean;
  setIsStock: (val: boolean) => void;
  
  // Units props (for stock control)
  unidadesMedida: Option[];
  unidadMedidaId: number;
  selectedUnidadName: string;
  onUnidadMedidaSelect: (id: number, name: string) => void;
  units: string;
  setUnits: (value: string) => void;
  
  // Current stock props
  currentStock: string;
  setCurrentStock: (value: string) => void;
  
  // Min quantity props
  minQuantity: string;
  setMinQuantity: (value: string) => void;
  
  // Storage props (for stock control)
  storageUnits: any[];
  selectedStorageUnit: any;
  onStorageUnitSelect: (unit: any) => void;

  // Production behavior props
  needProduction: boolean | null;
  setNeedProduction: (val: boolean | null) => void;
  hasIngredients: boolean;
  
  // Main ingredient's unidad de medida (to set as default)
  mainUnidadMedidaId?: number;
  
  // Control what parts to render
  view?: 'full' | 'toggleOnly' | 'unitsOnly';
}

const IngredientStockConfiguration: React.FC<IngredientStockConfigurationProps> = ({
  isStock,
  setIsStock,
  unidadesMedida,
  unidadMedidaId,
  selectedUnidadName,
  onUnidadMedidaSelect,
  units,
  setUnits,
  currentStock,
  setCurrentStock,
  minQuantity,
  setMinQuantity,
  storageUnits,
  selectedStorageUnit,
  onStorageUnitSelect,
  needProduction,
  setNeedProduction,
  hasIngredients,
  mainUnidadMedidaId,
  view = 'full'
}) => {

  // Set units to 1 by default, no user control
  React.useEffect(() => {
    if (isStock && units === '') {
      setUnits('1');
    }
  }, [isStock, units, setUnits]);

  // Set default unidad de medida to "Unidad" (id: 5) or main ingredient's unidad de medida
  React.useEffect(() => {
    if (isStock && unidadesMedida.length > 0 && unidadMedidaId === 0) {
      // First, try to find "Unidad" (id: 5) as the default
      const unidadDefault = unidadesMedida.find(um => um.id === 5);
      
      if (unidadDefault) {
        onUnidadMedidaSelect(unidadDefault.id, unidadDefault.name);
      } else if (mainUnidadMedidaId) {
        // If "Unidad" doesn't exist, fall back to main ingredient's unidad de medida
        const mainUnidadMedida = unidadesMedida.find(um => um.id === mainUnidadMedidaId);
        if (mainUnidadMedida) {
          onUnidadMedidaSelect(mainUnidadMedida.id, mainUnidadMedida.name);
        }
      }
    }
  }, [isStock, unidadesMedida, mainUnidadMedidaId, unidadMedidaId, onUnidadMedidaSelect]);

  // Set default storage unit (first one marked as default or first in list)
  React.useEffect(() => {
    if (isStock && storageUnits.length > 0 && !selectedStorageUnit) {
      // Look for a storage unit marked as is_default === true or is_default === 1
      const defaultStorageUnit = storageUnits.find(unit => 
        unit.is_default === true || unit.is_default === 1
      ) || storageUnits[0];
      
      if (defaultStorageUnit) {
        onStorageUnitSelect(defaultStorageUnit);
      }
    }
  }, [isStock, storageUnits, selectedStorageUnit, onStorageUnitSelect]);

  const handleCurrentStockChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || (!isNaN(Number(value)) && Number(value) >= 0)) {
      setCurrentStock(value);
    }
  };

  const handleMinQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || (!isNaN(Number(value)) && Number(value) >= 0)) {
      setMinQuantity(value);
    }
  };

  const getStorageUnitName = (unit: any) => unit?.name || unit?.storage_unit_name || 'Seleccionar';

  const renderToggle = () => (
    <div className="config-section">
      <div className="config-item">
        <div className="config-label">
          <FontAwesomeIcon icon={faWarehouse} className="config-icon" />
          <div className="label-content">
            <span className="label-title">Control de Stock</span>
            <span className="label-subtitle">Activar seguimiento de inventario para este ingrediente</span>
          </div>
        </div>
        <div className="config-toggle" onClick={() => setIsStock(!isStock)}>
          {isStock ? (
            <FaToggleOn className="toggle-icon active" />
          ) : (
            <FaToggleOff className="toggle-icon inactive" />
          )}
          <span className={`toggle-status ${isStock ? 'active' : 'inactive'}`}>
            {isStock ? 'Activado' : 'Desactivado'}
          </span>
        </div>
      </div>
    </div>
  );

  const renderUnitsSection = () => (
    isStock ? (
      <div className="config-section units-section">
        <div className="section-header">
          <h6 className="section-title">Configuración de Unidades</h6>
          <p className="section-subtitle">Define la unidad de medida, stock y almacenamiento</p>
        </div>
        
        {/* Primera fila: Los dos dropdowns (Unidad de medida y Almacenamiento) */}
        <Row className="g-0 units-row mb-3">
          <Col xl={5} lg={5} md={6} sm={12} xs={12} className="units-col">
            <div className="control-group">
              <label className="control-label">
                <i className="fas fa-ruler"></i>
                Unidad de medida
              </label>
              <Dropdown className="dropdown">
                <Dropdown.Toggle className="dropdown-toggle">
                  {selectedUnidadName}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {unidadesMedida.map((um) => (
                    <Dropdown.Item
                      key={um.id}
                      onClick={() => onUnidadMedidaSelect(um.id, um.name)}
                    >
                      {um.name}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </Col>

          <Col xl={5} lg={5} md={6} sm={12} xs={12} className="units-col">
            <div className="control-group">
              <label className="control-label">
                <i className="fas fa-warehouse"></i>
                Almacenamiento
              </label>
              <Dropdown className="dropdown">
                <Dropdown.Toggle className="dropdown-toggle">
                  {getStorageUnitName(selectedStorageUnit)}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {storageUnits.map((unit) => (
                    <Dropdown.Item
                      key={unit.storage_unit_id || unit.id}
                      onClick={() => onStorageUnitSelect(unit)}
                    >
                      {getStorageUnitName(unit)}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </Col>
        </Row>
        
        {/* Segunda fila: Los dos inputs de stock (Stock Actual y Stock Mínimo) */}
        <Row className="g-0 units-row">
          <Col xl={5} lg={5} md={6} sm={6} xs={12} className="units-col">
            <div className="control-group">
              <label className="control-label">
                <i className="fas fa-box"></i>
                Stock Actual
              </label>
              <Form.Control
                type="number"
                value={currentStock}
                placeholder="0"
                onChange={handleCurrentStockChange}
                className="modern-input compact"
                min="0"
                step="0.01"
              />
            </div>
          </Col>

          <Col xl={5} lg={5} md={6} sm={6} xs={12} className="units-col">
            <div className="control-group">
              <label className="control-label">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                Stock Mínimo
              </label>
              <Form.Control
                type="number"
                value={minQuantity}
                placeholder="0"
                onChange={handleMinQuantityChange}
                className="modern-input compact"
                min="0"
                step="0.01"
              />
            </div>
          </Col>
        </Row>
      </div>
    ) : null
  );

  const renderProductionSection = () => (
    isStock && hasIngredients ? (
      <div className="stock-management-question">
        <div className="question-header">
          <div className="mps-divider-items-modal"></div>
          <div className="mps-item-modal-label">
            ¿Requiere producción?
            {needProduction === null && <span className="required-indicator"> *</span>}
          </div>
        </div>
        <div className="options">
          <div 
            className={`stock-mode-card ${needProduction === true ? 'selected' : ''}`}
            onClick={() => setNeedProduction(true)}
          >
            <div className="icon">
              <FontAwesomeIcon icon={faCogs} />
            </div>
            <div className="text">
              <div className="title">Sí, requiere producción</div>
              <div className="subtitle">Se descuenta del stock propio al consumir</div>
            </div>
            <input
              type="radio"
              name="need_production"
              value="true"
              checked={needProduction === true}
              onChange={() => setNeedProduction(true)}
              className="visually-hidden-radio"
            />
          </div>
          
          <div 
            className={`stock-mode-card ${needProduction === false ? 'selected' : ''}`}
            onClick={() => setNeedProduction(false)}
          >
            <div className="icon">
              <FontAwesomeIcon icon={faBox} />
            </div>
            <div className="text">
              <div className="title">No requiere producción</div>
              <div className="subtitle">Descuenta sub-ingredientes directamente</div>
            </div>
            <input
              type="radio"
              name="need_production"
              value="false"
              checked={needProduction === false}
              onChange={() => setNeedProduction(false)}
              className="visually-hidden-radio"
            />
          </div>
        </div>
        
        {needProduction === null && (
          <div className="selection-required-message">
            <FaInfoCircle className="warning-icon" />
            <div className="warning-text">
              Debes seleccionar un comportamiento de producción antes de continuar.
            </div>
          </div>
        )}
        
        {needProduction !== null && (
          <div className="production-info">
            <FaInfoCircle className="info-icon" />
            <div className="info-text">
              {needProduction 
                ? "Este ingrediente se produce a partir de sus sub-ingredientes. Al consumirlo en un plato, solo se descuenta de su propio stock."
                : "Al consumir este ingrediente en un plato, se descontarán directamente sus sub-ingredientes."
              }
            </div>
          </div>
        )}
      </div>
    ) : null
  );

  return (
    <div className="ingredient-stock-configuration">
      <div className="config-header">
        <h6 className="config-title">Configuración de Stock</h6>
        <p className="config-subtitle">Define el control de inventario y comportamiento del ingrediente</p>
      </div>

      <div className="config-content">
        {view !== 'unitsOnly' && renderToggle()}
        {view !== 'toggleOnly' && renderUnitsSection()}
        {view !== 'toggleOnly' && renderProductionSection()}
      </div>
    </div>
  );
};

export default IngredientStockConfiguration;
